import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { checkoutService } from '../../services/checkoutService';
import { useAuth } from '../../contexts/AuthContext';
import { formatPrice } from '../../utils/formatters';

export default function OrderConfirmation() {
  const navigate = useNavigate();
  const { orderNumber } = useParams();
  const authContext = useAuth();
  const user = authContext?.user || null;

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ NEW: Calculate correct total including shipping and tax
  const calculatedTotal = useMemo(() => {
    if (!order) return 0;
    
    const subtotal = parseFloat(order?.subtotal) || 0;
    const shippingAmount = parseFloat(order?.shipping_amount) || 0;
    const taxAmount = parseFloat(order?.tax_amount) || 0;
    
    return subtotal + shippingAmount + taxAmount;
  }, [order]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderNumber) {
        setError('Order number not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const orderData = await checkoutService?.getOrderByNumber(orderNumber);
        console.log('OrderConfirmation: Fetched order data:', orderData); // NEW LOG
        setOrder(orderData);
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('Failed to load order details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderNumber]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address?.first_name} ${address?.last_name}, ${address?.address_line_1}${address?.address_line_2 ? ', ' + address?.address_line_2 : ''}, ${address?.city}, ${address?.state} ${address?.postal_code}, ${address?.country}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading order details...</p>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="text-center py-20">
              <Icon name="AlertCircle" size={64} className="mx-auto mb-6 text-red-500 opacity-50" />
              <h2 className="text-2xl font-semibold mb-4">Order Not Found</h2>
              <p className="text-muted-foreground mb-6">{error || 'Unable to load order details'}</p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate('/')} iconName="Home" iconPosition="left">
                  Go Home
                </Button>
                <Button onClick={() => navigate('/user-account-dashboard')} iconName="User" iconPosition="left" variant="outline">
                  View Orders
                </Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
              <Icon name="CheckCircle2" size={48} className="text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Order Confirmed!</h1>
            <p className="text-muted-foreground">
              Thank you for your purchase. Your order has been successfully placed.
            </p>
          </div>

          {/* Order Details Card */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold mb-1">Order Number</h2>
                <p className="text-2xl font-bold text-primary">{order?.order_number}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground mb-1">Order Date</p>
                <p className="font-medium">{formatDate(order?.created_at)}</p>
              </div>
            </div>

            {/* Order Status */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Order Status</p>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                    <Icon name="Clock" size={14} className="mr-1" />
                    {order?.status?.charAt(0)?.toUpperCase() + order?.status?.slice(1)}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Payment Status</p>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <Icon name="CreditCard" size={14} className="mr-1" />
                    {order?.payment_status?.charAt(0)?.toUpperCase() + order?.payment_status?.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="mb-6">
              <h3 className="font-semibold mb-3">Order Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">{formatPrice(order?.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="font-medium">
                    {order?.shipping_amount === 0 ? 'FREE' : formatPrice(order?.shipping_amount)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span className="font-medium">{formatPrice(order?.tax_amount)}</span>
                </div>
                <div className="border-t border-border pt-3">
                  <div className="flex justify-between">
                    <span className="font-semibold text-lg">Total</span>
                    <span className="text-2xl font-bold text-primary">{formatPrice(calculatedTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="mb-6">
              <h3 className="font-semibold mb-3">Items Ordered ({order?.items?.length})</h3>
              <div className="space-y-3">
                {order?.items?.map((item) => (
                  <div key={item?.id} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 bg-muted rounded flex-shrink-0 flex items-center justify-center">
                        <Icon name="Package" size={24} className="text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">{item?.name}</p>
                        {item?.sellerBusinessName && (
                          <p className="text-sm text-muted-foreground">Sold by: {item.sellerBusinessName}</p>
                        )}
                        <p className="text-sm text-muted-foreground">Qty: {item?.quantity}</p>
                        <p className="text-sm text-muted-foreground">{formatPrice(item?.unit_price)} each</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{formatPrice(item?.total_price)}</p>
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                        {item?.item_status?.charAt(0)?.toUpperCase() + item?.item_status?.slice(1)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping Address */}
            {order?.shipping_address && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Icon name="MapPin" size={18} />
                  Shipping Address
                </h3>
                <p className="text-sm text-muted-foreground">
                  {formatAddress(order?.shipping_address)}
                </p>
                {order?.shipping_address?.phone && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Phone: {order?.shipping_address?.phone}
                  </p>
                )}
              </div>
            )}

            {/* Billing Address */}
            {order?.billing_address && order?.billing_address?.id !== order?.shipping_address?.id && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Icon name="FileText" size={18} />
                  Billing Address
                </h3>
                <p className="text-sm text-muted-foreground">
                  {formatAddress(order?.billing_address)}
                </p>
              </div>
            )}

            {/* Order Notes */}
            {order?.notes && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Icon name="MessageSquare" size={18} />
                  Order Notes
                </h3>
                <p className="text-sm text-muted-foreground bg-muted p-3 rounded">
                  {order?.notes}
                </p>
              </div>
            )}
          </div>

          {/* Next Steps Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2 text-blue-900">
              <Icon name="Info" size={18} />
              What's Next?
            </h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <Icon name="Mail" size={16} className="mt-0.5 flex-shrink-0" />
                <span>You'll receive an order confirmation email shortly with your receipt and tracking details.</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="Package" size={16} className="mt-0.5 flex-shrink-0" />
                <span>Your order will be processed and shipped according to your selected delivery method.</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="Bell" size={16} className="mt-0.5 flex-shrink-0" />
                <span>We'll notify you when your order ships with tracking information.</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/model-catalog')} 
              iconName="ShoppingBag" 
              iconPosition="left"
              className="flex-1 sm:flex-none"
            >
              Continue Shopping
            </Button>
            <Button 
              onClick={() => navigate('/user-account-dashboard')} 
              iconName="User" 
              iconPosition="left" 
              variant="outline"
              className="flex-1 sm:flex-none"
            >
              View My Orders
            </Button>
            <Button 
              onClick={() => window?.print()} 
              iconName="Printer" 
              iconPosition="left" 
              variant="outline"
              className="flex-1 sm:flex-none"
            >
              Print Receipt
            </Button>
          </div>

          {/* Support Section */}
          <div className="mt-8 text-center p-6 bg-muted rounded-lg">
            <h3 className="font-semibold mb-2">Need Help?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              If you have any questions about your order, our support team is here to help.
            </p>
            <Button 
              onClick={() => navigate('/support-center')} 
              iconName="HelpCircle" 
              iconPosition="left" 
              variant="outline"
              size="sm"
            >
              Contact Support
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}